import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { sortObjectOptions } from '@/utils/sortOptions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '../utils/toast';
import { Search, Plus, Edit, XCircle, CreditCard, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageSubscriptionsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Add Subscription Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      toast.error('Please sign in');
      navigate('/signin');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    if (!['Super Admin', 'Exlva Admin', 'Admin', 'ExlvaAdmin'].includes(parsedUser.role)) {
      toast.error('Only Super Admin and Exlva Admins can access this page');
      navigate('/dashboard');
      return;
    }
    
    fetchSubscriptions();
  }, [navigate]);

  const fetchSubscriptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscriptions/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(response.data.subscriptions);
    } catch (error) {
      toast.error('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchDialogData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch hospitals
      const hospitalsRes = await axios.get(`${API}/subscriptions/hospitals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHospitals(hospitalsRes.data.hospitals);
      
      // Fetch plans
      const plansRes = await axios.get(`${API}/subscriptions/plans`);
      setPlans(plansRes.data.plans);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const calculateDates = async (hospitalId, cycle) => {
    if (!hospitalId || !cycle) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Get current subscriptions for this hospital
      const subsRes = await axios.get(`${API}/subscriptions/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const hospitalSubs = subsRes.data.subscriptions.filter(sub => 
        sub.hospital_admin_id === hospitalId && sub.status === 'active'
      );
      
      let startDate;
      if (hospitalSubs.length > 0) {
        // Find the latest expiry date
        const latestExpiry = hospitalSubs.reduce((latest, sub) => {
          const subExpiry = new Date(sub.expiry_date);
          return subExpiry > latest ? subExpiry : latest;
        }, new Date(0));
        
        startDate = new Date(latestExpiry);
        startDate.setDate(startDate.getDate() + 1); // Day after last subscription ends
      } else {
        // No existing subscription, start today
        startDate = new Date();
      }
      
      // Calculate end date based on billing cycle
      const endDate = new Date(startDate);
      if (cycle === 'monthly') {
        endDate.setMonth(endDate.getMonth() + 1);
      } else { // yearly
        endDate.setFullYear(endDate.getFullYear() + 1);
      }
      
      // Format dates for input fields
      setStartDate(startDate.toISOString().split('T')[0]);
      setEndDate(endDate.toISOString().split('T')[0]);
      
      toast.success(`Dates calculated: Start ${startDate.toDateString()}, End ${endDate.toDateString()}`);
    } catch (error) {
      // If error, use default dates
      const startDate = new Date();
      const endDate = new Date(startDate);
      if (cycle === 'monthly') {
        endDate.setMonth(endDate.getMonth() + 1);
      } else {
        endDate.setFullYear(endDate.getFullYear() + 1);
      }
      setStartDate(startDate.toISOString().split('T')[0]);
      setEndDate(endDate.toISOString().split('T')[0]);
      toast.info('Using default dates (current date + billing cycle)');
    }
  };

  const handleHospitalChange = (hospitalId) => {
    setSelectedHospital(hospitalId);
    // Calculate dates as soon as both hospital and billing cycle are available
    // Since billing cycle defaults to 'monthly', we can calculate immediately
    calculateDates(hospitalId, billingCycle);
  };

  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
    if (selectedHospital) {
      calculateDates(selectedHospital, cycle);
    }
  };

  const handleOpenDialog = () => {
    fetchDialogData();
    setSelectedHospital('');
    setSelectedPlan('');
    setBillingCycle('monthly');
    setStartDate('');
    setEndDate('');
    setDialogOpen(true);
  };

  const handleSubmitSubscription = async () => {
    // More explicit validation
    const missingFields = [];
    if (!selectedHospital) missingFields.push('Hospital');
    if (!selectedPlan) missingFields.push('Plan');  
    if (!billingCycle) missingFields.push('Billing Cycle');
    if (!startDate) missingFields.push('Start Date');
    
    if (missingFields.length > 0) {
      toast.error(`Please fill all required fields: ${missingFields.join(', ')}`);
      return;
    }

    if (endDate && new Date(endDate) <= new Date(startDate)) {
      toast.error('End date must be after start date');
      return;
    }

    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/subscriptions/create-for-hospital`,
        {
          hospital_admin_id: selectedHospital,
          plan_id: selectedPlan,
          billing_cycle: billingCycle,
          start_date: startDate,
          end_date: endDate || null
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            hospital_admin_id: selectedHospital,
            plan_id: selectedPlan,
            billing_cycle: billingCycle
          }
        }
      );
      
      toast.success('Subscription created successfully');
      setDialogOpen(false);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivate = async (subscriptionId, hospitalName) => {
    if (!window.confirm(`Are you sure you want to deactivate subscription for ${hospitalName}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/subscriptions/${subscriptionId}/deactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription deactivated successfully');
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate subscription');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredSubscriptions = subscriptions.filter(sub =>
    sub.hospital_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.plan_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading || !user) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading subscriptions...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Page Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-6">MANAGE SUBSCRIPTIONS</h1>

        {/* Statistics - Matching CRM Dashboard Style */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-blue-700 mb-2">Total Subscriptions</p>
                <h3 className="text-4xl font-bold text-blue-900">{subscriptions.length}</h3>
              </div>
              <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-blue-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-green-700 mb-2">Active</p>
                <h3 className="text-4xl font-bold text-green-900">
                  {subscriptions.filter(s => s.status === 'active').length}
                </h3>
              </div>
              <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-red-700 mb-2">Suspended/Expired</p>
                <h3 className="text-4xl font-bold text-red-900">
                  {subscriptions.filter(s => s.status !== 'active').length}
                </h3>
              </div>
              <div className="w-12 h-12 bg-red-200 rounded-lg flex items-center justify-center">
                <XCircle className="w-4 h-4 text-red-700" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-xl">
          {/* Header with Add Button */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>All Hospital Subscriptions</h2>
            <Button
              onClick={handleOpenDialog}
              className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Subscription
            </Button>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by hospital name or plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Subscriptions Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Hospital Name</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Licenses</TableHead>
                  <TableHead>Expiry Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No subscriptions found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubscriptions.map((sub) => (
                    <TableRow key={sub.subscription_id}>
                      <TableCell className="font-medium">{sub.hospital_name}</TableCell>
                      <TableCell>{sub.plan_name}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(sub.status)}>
                          {sub.status.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {sub.active_staff_count} / {sub.total_licenses === 999999 ? '∞' : sub.total_licenses}
                      </TableCell>
                      <TableCell>{formatDate(sub.expiry_date)}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/subscriptions/edit/${sub.subscription_id}`)}
                            data-tooltip="Edit Subscription"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {sub.status === 'active' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeactivate(sub.subscription_id, sub.hospital_name)}
                              data-tooltip="Deactivate"
                            >
                              <XCircle className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Add Subscription Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Create New Subscription</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0 mt-4">
          <div className="space-y-4 py-4">
            {/* Hospital Selection */}
            <div>
              <Label>Select Hospital *</Label>
              <Select value={selectedHospital} onValueChange={handleHospitalChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a hospital" />
                </SelectTrigger>
                <SelectContent>
                  {sortObjectOptions(hospitals, 'institution_name').map(hospital => (
                    <SelectItem key={hospital.id} value={hospital.id}>
                      {hospital.institution_name} ({hospital.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Plan Selection */}
            <div>
              <Label>Select Plan *</Label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent>
                  {sortObjectOptions(plans, 'plan_name').map(plan => (
                    <SelectItem key={plan.plan_id} value={plan.plan_id}>
                      {plan.plan_name} - ₹{billingCycle === 'monthly' ? plan.price_monthly?.toLocaleString() : plan.price_yearly?.toLocaleString()}/{billingCycle === 'monthly' ? 'month' : 'year'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Billing Cycle */}
            <div>
              <Label className="mb-2 block">Billing Cycle *</Label>
              <div className="grid grid-cols-2 gap-3">
                <div
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    billingCycle === 'monthly'
                      ? 'border-[#0056B3] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleBillingCycleChange('monthly')}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Monthly</span>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      billingCycle === 'monthly' ? 'border-[#0056B3]' : 'border-gray-300'
                    }`}>
                      {billingCycle === 'monthly' && (
                        <div className="w-2 h-2 rounded-full bg-[#0056B3]"></div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    billingCycle === 'yearly'
                      ? 'border-[#0056B3] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleBillingCycleChange('yearly')}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Yearly</span>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      billingCycle === 'yearly' ? 'border-[#0056B3]' : 'border-gray-300'
                    }`}>
                      {billingCycle === 'yearly' && (
                        <div className="w-2 h-2 rounded-full bg-[#0056B3]"></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Start Date */}
            <div>
              <Label>Subscription Start Date *</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 bg-gray-50"
                readOnly={selectedHospital} // Read-only when hospital is selected (auto-calculated)
              />
              <p className="text-xs text-gray-500 mt-1">
                {selectedHospital 
                  ? 'Auto-calculated based on existing subscriptions or current date' 
                  : 'Select hospital first to auto-calculate start date'}
              </p>
            </div>

            {/* End Date */}
            <div>
              <Label>Subscription End Date *</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 bg-gray-50"
                readOnly={selectedHospital && billingCycle} // Read-only when auto-calculated
              />
              <p className="text-xs text-gray-500 mt-1">
                {selectedHospital && billingCycle
                  ? `Auto-calculated: ${billingCycle === 'monthly' ? '1 month' : '1 year'} from start date`
                  : 'Select hospital and billing cycle to auto-calculate end date'}
              </p>
            </div>

            {/* Price Summary */}
            {selectedPlan && plans.find(p => p.plan_id === selectedPlan) && (
              <div className="bg-gray-50 rounded-lg p-4 border">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Plan Price:</span>
                  <span className="font-semibold">
                    ₹{(billingCycle === 'monthly' 
                      ? plans.find(p => p.plan_id === selectedPlan).price_monthly 
                      : plans.find(p => p.plan_id === selectedPlan).price_yearly)?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">GST (18%):</span>
                  <span className="font-semibold">
                    ₹{(billingCycle === 'monthly' 
                      ? plans.find(p => p.plan_id === selectedPlan).price_monthly * 0.18
                      : plans.find(p => p.plan_id === selectedPlan).price_yearly * 0.18)?.toLocaleString()}
                  </span>
                </div>
                <div className="border-t pt-2 flex justify-between items-center">
                  <span className="font-bold">Total:</span>
                  <span className="font-bold text-lg" style={{ color: '#0056B3' }}>
                    ₹{(billingCycle === 'monthly' 
                      ? plans.find(p => p.plan_id === selectedPlan).price_monthly * 1.18
                      : plans.find(p => p.plan_id === selectedPlan).price_yearly * 1.18)?.toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={formLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmitSubscription}
              className="bg-[#0056B3] hover:bg-[#0056B3]/90"
              disabled={formLoading || !selectedHospital || !selectedPlan || !billingCycle || !startDate}
            >
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ManageSubscriptionsPage;
